﻿import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Typography,
  Box,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  gridStringOrNumberComparator,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import {
  selectConnection,
  setHoveredWorkflow,
  selectHoveredWorkflow,
  setOpenWorkflowTab,
} from "@/features/homepage/homeSlice";
import { setSelectedWorkflow } from "@/features/homepage/workflowManagerSessionSlice";
import { setManagerSearchString } from "@/features/homepage/workflowManagerSessionSlice";
import { createAlert } from "@/features/homepage/alertSliceList";
import dayjs from "dayjs";
import "./RecordsTab.css";
import { useTranslation } from "react-i18next";
import {
  SearchIcon,
  ErrorIcon,
  SplitIcon,
  ConsolidateIcon,
} from "../../../ImageRepo";
import {
  getRecords,
  selectRecords,
  selectIsDataLoaded,
  setSelectedRows,
  selectOptionalWorkflows,
  selectWorkflowToEnabledConnectionPairMap,
} from "@/features/homepage/recordsSlice";
import {
  ShepCheckbox,
  ShepPagination,
  ShepDataGridPro,
} from "@/components/GenericComponents/ShepComponents";
import {
  selectHomeDateFilter,
  setHomeDateFilter,
} from "@/features/dateFilterSlice";

const RecordCell = (params) => {
  const { t } = useTranslation("homepage");

  const additionalClass =
    params.hoveredOperation === params.colDef.operationId
      ? params.hoveredColumn === params.colDef.field
        ? "hovered "
        : ""
      : "";
  const isSplit = params.row?.IsSplit;
  const isConsolidate = params.row?.IsConsolidate;
  const dispatch = useDispatch();
  //console.log(connectionProfile)
  //useEffect(() => {
  //const egg =     dispatch(fetchConnectionProfile({ operationId: params.row.operationId, connectionPairId: params.row.connectionIdS }));
  //}, [dispatch]);

  const receiveOrPostDate = () => {
    if (params.showPostedDate && params.row?.PostedDate)
      return dayjs(params.row?.PostedDate)
        .utc(true)
        .local()
        .format("YYYY.MM.DD HH:mm");
    else if (!params.showPostedDate && params.row?.ReceivedDate)
      return dayjs(params.row?.ReceivedDate)
        .utc(true)
        .local()
        .format("YYYY.MM.DD HH:mm");
    else return "-";
  };

  return (
    <Button
      className={
        "recordCell " +
        additionalClass +
        (params.noAutomationMessage ? "noAutomation" : "")
      }
      onClick={
        params.row === null || params.row.OperationId === null
          ? () => {
              dispatch(
                createAlert({
                  status: "Info",
                  message: "The record has no workflow associated with it yet.",
                }),
              );
            }
          : () =>
              params.onClick({
                operationId: params.row.OperationId,
                reference: params.row.Reference,
                date: params.row.LogTimeUTC,
              })
      }
    >
      <div className="recordContent">
        {params.noAutomationMessage ? (
          <div className="noAutomation">
            <Box display="flex" alignItems="center" position="relative">
              <ErrorIcon className="errorIcon" />
              <Typography variant="body1">
                {t("records.AutomationDisabled", "Automation is off")}
              </Typography>
            </Box>
            <Typography variant="contentAccent5">
              {t("records.AutomationDisabledCallToAction", "Go to Manager")}
            </Typography>
          </div>
        ) : (
          <div>
            <Typography> {params.row?.Reference ?? "-"}</Typography>
            <Typography className="logTime">{receiveOrPostDate()}</Typography>
            {isSplit && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "2.5rem",
                }}
              >
                <SplitIcon className="recordCell-split-icon" />
                <Typography
                  className="recordCell-split-icon-label"
                  sx={{ marginLeft: "0.5rem" }}
                >
                  Split
                </Typography>
              </Box>
            )}
            {isConsolidate && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "2.5rem",
                }}
              >
                <ConsolidateIcon className="recordCell-split-icon" />
                <Typography
                  className="recordCell-split-icon-label"
                  sx={{ marginLeft: "0.5rem" }}
                >
                  Consolidate
                </Typography>
              </Box>
            )}
          </div>
        )}
      </div>
    </Button>
  );
};

const PinnedRecordCell = (params) => {
  return (
    <div className="recordCell">
      <div className="recordContent">
        <Typography>{params.row?.ConnectionName ?? "-"}</Typography>
      </div>
    </div>
  );
};
const RecordHeader = ({ params, hoveredOperation, setColumn }) => {
  const dispatch = useDispatch();

  const handleMouseEnter = () => {
    dispatch(setSelectedWorkflow({ selectedWorkflow: 0 }));
    dispatch(
      setHoveredWorkflow({
        hoveredWorkflow: params.colDef.operationId,
      }),
    );
    setColumn(params.colDef.field);
  };

  const handleMouseLeave = () => {
    dispatch(
      setHoveredWorkflow({
        hoveredWorkflow: 0,
      }),
    );
  };

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={
        "headerDiv " +
        (hoveredOperation === params.colDef.operationId ? "hovered" : "")
      }
    >
      <Typography sx={{ wordBreak: "break-word", whiteSpace: "pre-line" }}>
        {params.colDef.headerName}
      </Typography>
    </div>
  );
};

const determineNoAutomationWarningDisplay = (
  currentOperationId,
  currentOperationRow,
  prevOperationRow,
  optionalWorkflows,
  workflowToEnabledConnectionPairMap,
) => {
  // Logic to determine if no automation warning should be displayed
  if (
    currentOperationRow ||
    !prevOperationRow ||
    !optionalWorkflows ||
    !workflowToEnabledConnectionPairMap
  ) {
    return false;
  }

  const currentWorkflowOptional = optionalWorkflows.find(
    (workflow) => workflow.operationId === currentOperationId,
  );

  if (currentWorkflowOptional) {
    return false;
  }

  const connectionPairEnabledForThisOperation =
    workflowToEnabledConnectionPairMap[currentOperationId].includes(
      prevOperationRow.ConnectionPairId,
    );

  return !connectionPairEnabledForThisOperation;
};

const RecordGroupGrid = ({ records }) => {
  const { t } = useTranslation("homepage");
  const dispatch = useDispatch();
  const [hoveredColumn, setHoveredColumn] = useState("");
  const hoveredState = useSelector(selectHoveredWorkflow);
  const isDataLoaded = useSelector(selectIsDataLoaded);
  const [selectedRows, setSelectedRowGroupId] = useState([]);
  const optionalWorkflows = useSelector(selectOptionalWorkflows);
  const workflowToEnabledConnectionPairMap = useSelector(
    selectWorkflowToEnabledConnectionPairMap,
  );

  const openRecordInWorkflowGrid = ({ operationId, reference, date }) => {
    dispatch(setSelectedWorkflow({ selectedWorkflow: operationId }));
    dispatch(
      setManagerSearchString({
        searchString: reference,
        searchAll: true,
      }),
    );
    dispatch(setOpenWorkflowTab({ tabNum: 0 }));
  };

  const columns = React.useMemo(() => [
    {
      ...GRID_CHECKBOX_SELECTION_COL_DEF,
      minWidth: 40,
      minHeight: 80,
      sortable: false,
    },
    {
      field: "customer",
      headerName: t("records.customer", "Customer"),
      minWidth: 140,
      minHeight: 80,
      sortable: true,
      valueGetter: (params) => {
        return params.row.CustomerOrder;
      },
      sortComparator: (v1, v2) => {
        const val = gridStringOrNumberComparator(
          v1?.ConnectionName ?? "",
          v2?.ConnectionName ?? "",
        );
        return val;
      },
      renderCell: (params) => (
        <PinnedRecordCell row={params.row.CustomerOrder} />
      ),
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) {
          return "";
        }
        return params.value.ConnectionName ?? "";
      },
    },
    {
      field: "CustomerOrder",
      headerName: t(
        "records.CustomerOrderReceived",
        "Customer Order\n Received",
      ),
      minWidth: 324,
      minHeight: 80,
      operationId: 101010,
      sortable: true,
      sortComparator: (v1, v2) => {
        const val = gridStringOrNumberComparator(
          v1?.Reference ?? "",
          v2?.Reference ?? "",
        );
        return val;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) {
          return "";
        }
        return params.value.Reference + " - " + params.value.LogTimeUTC;
      },
      renderCell: (params) => (
        <RecordCell
          row={params.row.CustomerOrder}
          onClick={openRecordInWorkflowGrid}
          hoveredOperation={hoveredState}
          hoveredColumn={hoveredColumn}
          colDef={params.colDef}
          showPostedDate={false}
        />
      ),
      renderHeader: (params) => (
        <RecordHeader
          params={params}
          hoveredOperation={hoveredState}
          setColumn={setHoveredColumn}
        />
      ),
    },
    {
      field: "SalesOrder",
      headerName: t(
        "workflow-summary.artifact-name.salesOrderPosted",
        "Sales Order\n Posted",
      ),
      minWidth: 324,
      minHeight: 80,
      operationId: 101010,
      sortable: true,
      sortComparator: (v1, v2) => {
        const val = gridStringOrNumberComparator(
          v1?.Reference ?? "",
          v2?.Reference ?? "",
        );
        return val;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) {
          return "";
        }
        return params.value.Reference + " - " + params.value.LogTimeUTC;
      },
      renderCell: (params) => (
        <RecordCell
          row={params.row.SalesOrder}
          noAutomationMessage={determineNoAutomationWarningDisplay(
            101010,
            params.row.SalesOrder,
            params.row.CustomerOrder,
            optionalWorkflows,
            workflowToEnabledConnectionPairMap,
          )}
          onClick={openRecordInWorkflowGrid}
          hoveredOperation={hoveredState}
          hoveredColumn={hoveredColumn}
          colDef={params.colDef}
          showPostedDate={true}
        />
      ),
      renderHeader: (params) => (
        <RecordHeader
          params={params}
          hoveredOperation={hoveredState}
          setColumn={setHoveredColumn}
        />
      ),
    },
    {
      field: "CustomerOrderAcknowledgement",
      headerName: t(
        "records.poacknowledgement",
        "Customer Order \n Acknowledgement Sent",
      ),
      minWidth: 324,
      minHeight: 80,
      operationId: 101040,
      sortable: true,
      sortComparator: (v1, v2) => {
        const val = gridStringOrNumberComparator(
          v1?.Reference ?? "",
          v2?.Reference ?? "",
        );
        return val;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) {
          return "";
        }
        return params.value.Reference + " - " + params.value.LogTimeUTC;
      },
      renderCell: (params) => (
        <RecordCell
          row={params.row.CustomerOrderAcknowledgement}
          onClick={openRecordInWorkflowGrid}
          hoveredOperation={hoveredState}
          hoveredColumn={hoveredColumn}
          colDef={params.colDef}
          showPostedDate={true}
        />
      ),
      renderHeader: (params) => (
        <RecordHeader
          params={params}
          hoveredOperation={hoveredState}
          setColumn={setHoveredColumn}
        />
      ),
    },
    {
      field: "PurchaseOrderChange",
      headerName: t("records.pochange", "Customer Order\n Change Received"),
      minWidth: 324,
      minHeight: 80,
      sortable: true,
      operationId: 101020,
      sortComparator: (v1, v2) => {
        const val = gridStringOrNumberComparator(
          v1?.Reference ?? "",
          v2?.Reference ?? "",
        );
        return val;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) {
          return "";
        }
        return params.value.Reference + " - " + params.value.LogTimeUTC;
      },
      renderCell: (params) => (
        <RecordCell
          row={params.row.PurchaseOrderChange}
          onClick={openRecordInWorkflowGrid}
          hoveredOperation={hoveredState}
          hoveredColumn={hoveredColumn}
          colDef={params.colDef}
          showPostedDate={false}
        />
      ),
      renderHeader: (params) => (
        <RecordHeader
          params={params}
          hoveredOperation={hoveredState}
          setColumn={setHoveredColumn}
        />
      ),
    },
    {
      field: "POChangeAcknowledgement",
      headerName: t(
        "records.pochangeacknowledgement",
        "Customer Order Change \n Acknowledgement Sent",
      ),
      minWidth: 324,
      minHeight: 80,
      operationId: 101050,
      sortable: true,
      sortComparator: (v1, v2) => {
        const val = gridStringOrNumberComparator(
          v1?.Reference ?? "",
          v2?.Reference ?? "",
        );
        return val;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) {
          return "";
        }
        return params.value.Reference + " - " + params.value.LogTimeUTC;
      },
      renderCell: (params) => (
        <RecordCell
          row={params.row.POChangeAcknowledgement}
          onClick={openRecordInWorkflowGrid}
          hoveredOperation={hoveredState}
          hoveredColumn={hoveredColumn}
          colDef={params.colDef}
          showPostedDate={true}
        />
      ),
      renderHeader: (params) => (
        <RecordHeader
          params={params}
          hoveredOperation={hoveredState}
          setColumn={setHoveredColumn}
        />
      ),
    },
    {
      field: "CustomerOrderStatus",
      headerName: t(
        "records.orderstatusreport",
        "Customer Order \n Status Sent",
      ),
      flex: 1,
      minWidth: 324,
      minHeight: 80,
      operationId: 101030,
      sortable: true,
      sortComparator: (v1, v2) => {
        const val = gridStringOrNumberComparator(
          v1?.Reference ?? "",
          v2?.Reference ?? "",
        );
        return val;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) {
          return "";
        }
        return params.value.Reference + " - " + params.value.LogTimeUTC;
      },
      renderCell: (params) => (
        <RecordCell
          row={params.row.CustomerOrderStatus}
          onClick={openRecordInWorkflowGrid}
          hoveredOperation={hoveredState}
          hoveredColumn={hoveredColumn}
          colDef={params.colDef}
          showPostedDate={true}
        />
      ),
      renderHeader: (params) => (
        <RecordHeader
          params={params}
          hoveredOperation={hoveredState}
          setColumn={setHoveredColumn}
        />
      ),
    },
    {
      field: "PickOrder",
      headerName: t("records.pickOrder", "Pick Order\n Read"),
      flex: 1,
      minWidth: 324,
      minHeight: 80,
      operationId: 102010,
      sortable: true,
      sortComparator: (v1, v2) => {
        const val = gridStringOrNumberComparator(
          v1?.Reference ?? "",
          v2?.Reference ?? "",
        );
        return val;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) {
          return "";
        }
        return params.value.Reference + " - " + params.value.LogTimeUTC;
      },
      renderCell: (params) => (
        <RecordCell
          row={params.row.PickOrder}
          onClick={openRecordInWorkflowGrid}
          hoveredOperation={hoveredState}
          hoveredColumn={hoveredColumn}
          colDef={params.colDef}
          showPostedDate={false}
        />
      ),
      renderHeader: (params) => (
        <RecordHeader
          params={params}
          hoveredOperation={hoveredState}
          setColumn={setHoveredColumn}
        />
      ),
    },
    {
      field: "WarehousePickOrder",
      headerName: t("records.pickOrder", "Warehouse \nPick Order Sent"),
      flex: 1,
      minWidth: 324,
      minHeight: 80,
      operationId: 102010,
      sortable: true,
      sortComparator: (v1, v2) => {
        const val = gridStringOrNumberComparator(
          v1?.Reference ?? "",
          v2?.Reference ?? "",
        );
        return val;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) {
          return "";
        }
        return params.value.Reference + " - " + params.value.LogTimeUTC;
      },
      renderCell: (params) => (
        <RecordCell
          row={params.row.WarehousePickOrder}
          noAutomationMessage={determineNoAutomationWarningDisplay(
            102010,
            params.row.WarehousePickOrder,
            params.row.PickOrder,
            optionalWorkflows,
            workflowToEnabledConnectionPairMap,
          )}
          onClick={openRecordInWorkflowGrid}
          hoveredOperation={hoveredState}
          hoveredColumn={hoveredColumn}
          colDef={params.colDef}
          showPostedDate={true}
        />
      ),
      renderHeader: (params) => (
        <RecordHeader
          params={params}
          hoveredOperation={hoveredState}
          setColumn={setHoveredColumn}
        />
      ),
    },
    {
      field: "PackedOrder",
      headerName: t("records.packingShipment", "Packed Order\n Received"),
      minWidth: 324,
      minHeight: 80,
      sortable: true,
      operationId: 102051,
      sortComparator: (v1, v2) => {
        const val = gridStringOrNumberComparator(
          v1?.Reference ?? "",
          v2?.Reference ?? "",
        );
        return val;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) {
          return "";
        }
        return params.value.Reference + " - " + params.value.LogTimeUTC;
      },
      renderCell: (params) => (
        <RecordCell
          row={params.row.PackedOrder}
          onClick={openRecordInWorkflowGrid}
          hoveredOperation={hoveredState}
          hoveredColumn={hoveredColumn}
          colDef={params.colDef}
          showPostedDate={false}
        />
      ),
      renderHeader: (params) => (
        <RecordHeader
          params={params}
          hoveredOperation={hoveredState}
          setColumn={setHoveredColumn}
        />
      ),
    },
    {
      field: "CustomerRoutingRequest",
      headerName: t(
        "records.routingRequest",
        "Customer Routing \n Request Sent",
      ),
      minWidth: 324,
      minHeight: 80,
      sortable: true,
      operationId: 102030,
      sortComparator: (v1, v2) => {
        const val = gridStringOrNumberComparator(
          v1?.Reference ?? "",
          v2?.Reference ?? "",
        );
        return val;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) {
          return "";
        }
        return params.value.Reference + " - " + params.value.LogTimeUTC;
      },
      renderCell: (params) => (
        <RecordCell
          row={params.row.CustomerRoutingRequest}
          onClick={openRecordInWorkflowGrid}
          hoveredOperation={hoveredState}
          hoveredColumn={hoveredColumn}
          colDef={params.colDef}
          showPostedDate={true}
        />
      ),
      renderHeader: (params) => (
        <RecordHeader
          params={params}
          hoveredOperation={hoveredState}
          setColumn={setHoveredColumn}
        />
      ),
    },
    {
      field: "CustomerRoutingConfirmation",
      headerName: t(
        "records.routingConfirmation",
        "Customer Routing \n Confirmation Posted",
      ),
      sortable: true,
      minWidth: 324,
      minHeight: 80,
      operationId: 102040,
      sortComparator: (v1, v2) => {
        const val = gridStringOrNumberComparator(
          v1?.Reference ?? "",
          v2?.Reference ?? "",
        );
        return val;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) {
          return "";
        }
        return params.value.Reference + " - " + params.value.LogTimeUTC;
      },
      renderCell: (params) => (
        <RecordCell
          row={params.row.CustomerRoutingConfirmation}
          onClick={openRecordInWorkflowGrid}
          hoveredOperation={hoveredState}
          hoveredColumn={hoveredColumn}
          colDef={params.colDef}
          showPostedDate={false}
        />
      ),
      renderHeader: (params) => (
        <RecordHeader
          params={params}
          hoveredOperation={hoveredState}
          setColumn={setHoveredColumn}
        />
      ),
    },
    {
      field: "CustomerShipmentNotice",
      headerName: t(
        "records.shipmentNotice",
        "Customer Shipment \n Notice Sent",
      ),
      sortable: true,
      minWidth: 324,
      minHeight: 80,
      operationId: 102060,
      sortComparator: (v1, v2) => {
        const val = gridStringOrNumberComparator(
          v1?.Reference ?? "",
          v2?.Reference ?? "",
        );
        return val;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) {
          return "";
        }
        return params.value.Reference + " - " + params.value.LogTimeUTC;
      },
      renderCell: (params) => (
        <RecordCell
          row={params.row.CustomerShipmentNotice}
          noAutomationMessage={determineNoAutomationWarningDisplay(
            102060,
            params.row.CustomerShipmentNotice,
            params.row.PackedOrder,
            optionalWorkflows,
            workflowToEnabledConnectionPairMap,
          )}
          onClick={openRecordInWorkflowGrid}
          hoveredOperation={hoveredState}
          hoveredColumn={hoveredColumn}
          colDef={params.colDef}
          showPostedDate={true}
        />
      ),
      renderHeader: (params) => (
        <RecordHeader
          params={params}
          hoveredOperation={hoveredState}
          setColumn={setHoveredColumn}
        />
      ),
    },
    {
      field: "ShipmentRecord",
      headerName: t("records.shipmentRecord", "Shipment Record \n Posted"),
      sortable: true,
      minWidth: 324,
      minHeight: 80,
      operationId: 102051,
      sortComparator: (v1, v2) => {
        const val = gridStringOrNumberComparator(
          v1?.Reference ?? "",
          v2?.Reference ?? "",
        );
        return val;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) {
          return "";
        }
        return params.value.Reference + " - " + params.value.LogTimeUTC;
      },
      renderCell: (params) => (
        <RecordCell
          row={params.row.ShipmentRecord}
          onClick={openRecordInWorkflowGrid}
          hoveredOperation={hoveredState}
          hoveredColumn={hoveredColumn}
          colDef={params.colDef}
          showPostedDate={true}
        />
      ),
      renderHeader: (params) => (
        <RecordHeader
          params={params}
          hoveredOperation={hoveredState}
          setColumn={setHoveredColumn}
        />
      ),
    },
    {
      field: "InvoiceRecord",
      headerName: t("records.invoiceRecord", "Invoice Record\n Posted"),
      sortable: true,
      minWidth: 324,
      minHeight: 80,
      operationId: 103010,
      sortComparator: (v1, v2) => {
        const val = gridStringOrNumberComparator(
          v1?.Reference ?? "",
          v2?.Reference ?? "",
        );
        return val;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) {
          return "";
        }
        return params.value.Reference + " - " + params.value.LogTimeUTC;
      },
      renderCell: (params) => (
        <RecordCell
          row={params.row.InvoiceRecord}
          onClick={openRecordInWorkflowGrid}
          hoveredOperation={hoveredState}
          hoveredColumn={hoveredColumn}
          colDef={params.colDef}
          showPostedDate={false}
        />
      ),
      renderHeader: (params) => (
        <RecordHeader
          params={params}
          hoveredOperation={hoveredState}
          setColumn={setHoveredColumn}
        />
      ),
    },
    {
      field: "CustomerInvoice",
      headerName: t("records.customerInvoice", "Customer Invoice \n Sent"),
      sortable: true,
      minWidth: 324,
      minHeight: 80,
      operationId: 103010,
      sortComparator: (v1, v2) => {
        const val = gridStringOrNumberComparator(
          v1?.Reference ?? "",
          v2?.Reference ?? "",
        );
        return val;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) {
          return "";
        }
        return params.value.Reference + " - " + params.value.LogTimeUTC;
      },
      renderCell: (params) => (
        <RecordCell
          row={params.row.CustomerInvoice}
          noAutomationMessage={determineNoAutomationWarningDisplay(
            103010,
            params.row.CustomerInvoice,
            params.row.InvoiceRecord,
            optionalWorkflows,
            workflowToEnabledConnectionPairMap,
          )}
          onClick={openRecordInWorkflowGrid}
          hoveredOperation={hoveredState}
          hoveredColumn={hoveredColumn}
          colDef={params.colDef}
          showPostedDate={true}
        />
      ),
      renderHeader: (params) => (
        <RecordHeader
          params={params}
          hoveredOperation={hoveredState}
          setColumn={setHoveredColumn}
        />
      ),
    },
    {
      field: "CreditNote",
      headerName: t("records.creditNote", "Credit Note \n Sent"),
      sortable: true,
      minWidth: 324,
      minHeight: 80,
      operationId: 103020,
      sortComparator: (v1, v2) => {
        const val = gridStringOrNumberComparator(
          v1?.Reference ?? "",
          v2?.Reference ?? "",
        );
        return val;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) {
          return "";
        }
        return params.value.Reference + " - " + params.value.LogTimeUTC;
      },
      renderCell: (params) => (
        <RecordCell
          row={params.row.CreditNote}
          onClick={openRecordInWorkflowGrid}
          hoveredOperation={hoveredState}
          hoveredColumn={hoveredColumn}
          colDef={params.colDef}
          showPostedDate={false}
        />
      ),
      renderHeader: (params) => (
        <RecordHeader
          params={params}
          hoveredOperation={hoveredState}
          setColumn={setHoveredColumn}
        />
      ),
    },
    {
      field: "StockTransfer",
      headerName: t("records.stockTransfer", "Stock Transfer \n Read"),
      sortable: true,
      minWidth: 324,
      minHeight: 80,
      operationId: 104030,
      sortComparator: (v1, v2) => {
        const val = gridStringOrNumberComparator(
          v1?.Reference ?? "",
          v2?.Reference ?? "",
        );
        return val;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) {
          return "";
        }
        return params.value.Reference + " - " + params.value.LogTimeUTC;
      },
      renderCell: (params) => (
        <RecordCell
          row={params.row.StockTransfer}
          onClick={openRecordInWorkflowGrid}
          hoveredOperation={hoveredState}
          hoveredColumn={hoveredColumn}
          colDef={params.colDef}
          showPostedDate={true}
        />
      ),
      renderHeader: (params) => (
        <RecordHeader
          params={params}
          hoveredOperation={hoveredState}
          setColumn={setHoveredColumn}
        />
      ),
    },
    {
      field: "ReceivingWarehouseStockTransfer",
      headerName: t(
        "records.receivingWarehouseStockTransfer",
        "Receiving Warehouse \n Stock Transfer Sent",
      ),
      sortable: true,
      minWidth: 324,
      minHeight: 80,
      operationId: 104030,
      sortComparator: (v1, v2) => {
        const val = gridStringOrNumberComparator(
          v1?.Reference ?? "",
          v2?.Reference ?? "",
        );
        return val;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) {
          return "";
        }
        return params.value.Reference + " - " + params.value.LogTimeUTC;
      },
      renderCell: (params) => (
        <RecordCell
          row={params.row.ReceivingWarehouseStockTransfer}
          onClick={openRecordInWorkflowGrid}
          hoveredOperation={hoveredState}
          hoveredColumn={hoveredColumn}
          colDef={params.colDef}
          showPostedDate={true}
        />
      ),
      renderHeader: (params) => (
        <RecordHeader
          params={params}
          hoveredOperation={hoveredState}
          setColumn={setHoveredColumn}
        />
      ),
    },
  ]);
  return (
    <ShepDataGridPro
      isDataLoaded={isDataLoaded}
      rows={records}
      columns={columns}
      columnHeaderHeight={80}
      rowHeight={80}
      pagination
      initialState={{
        pagination: {
          paginationModel: { pageSize: 12, page: 0 },
        },
        pinnedColumns: {
          left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, "customer"],
        },
      }}
      slots={{
        baseCheckbox: ShepCheckbox,
        pagination: ShepPagination,
        toolbar: CustomToolbar,
      }}
      whiteHeader={true}
      checkboxSelection={true}
      disableColumnMenu={true}
      disableColumnFilter={true}
      hideSortIcons={true}
      disableColumnReorder={true}
      disableColumnPinning={false}
      disableColumnSelector={true}
      disableDensitySelector={true}
      hideFooterSelectedRowCount={true}
      onRowSelectionModelChange={(newSelectionModel) => {
        dispatch(setSelectedRows({ selectedRows: newSelectionModel }));
      }}
      getRowId={(params) => {
        return params.GroupId;
      }}
    />
  );
};

const RecordsTab = () => {
  const dispatch = useDispatch();
  const records = useSelector(selectRecords);
  const selectedConnection = useSelector(selectConnection);
  var { startDate, endDate } = useSelector(selectHomeDateFilter) || {};

  useEffect(() => {
    getRecordsData();
  }, [startDate, endDate, selectedConnection]);

  useEffect(() => {
    //just run once on tab open
    //deselect any workflows if selected - interferes with hover ui
    dispatch(setSelectedWorkflow({ selectedWorkflow: 0 }));
  });
  const getRecordsData = () => {
    const defaultStartDays = 7;

    if (startDate === "" && endDate === "") {
      startDate = dayjs().subtract(defaultStartDays, "day").startOf("day");
      endDate = dayjs().add(1, "day").startOf("day");
    } else if (endDate === null) {
      startDate = startDate.startOf("day");
      endDate = startDate.endOf("day");
    }

    dispatch(
      getRecords({
        selectedConnectionId: selectedConnection?.ConnectionId ?? -1,
        startDate: startDate,
        endDate: endDate,
      }),
    );
  };

  return (
    <>
      <Box className="gridContainer">
        <RecordGroupGrid records={records} />
      </Box>
    </>
  );
};

const Searchbar = () => {
  const [input, setInput] = useState("");
  const dispatch = useDispatch();
  const selectedConnection = useSelector(selectConnection);
  var { startDate, endDate } = useSelector(selectHomeDateFilter) || {};

  const triggerSearch = (searchString) => {
    const defaultStartDays = 7;

    if (startDate === "" && endDate === "") {
      startDate = dayjs().subtract(defaultStartDays, "day").startOf("day");
      endDate = dayjs().add(1, "day").startOf("day");
    } else if (endDate === null) {
      startDate = startDate.startOf("day");
      endDate = startDate.endOf("day");
    }

    dispatch(
      getRecords({
        selectedConnectionId: selectedConnection?.ConnectionId ?? -1,
        startDate: startDate,
        endDate: endDate,
        searchString: searchString,
      }),
    );
  };
  useEffect(() => {
    setInput("");
  }, [selectedConnection, startDate, endDate]); // Clear on connection and date change

  const handleTextChange = (e) => {
    setInput(e.target.value);
    if (e.target.value === "") {
      triggerSearch(""); // If the input value becomes empty, then refresh the grid
    }
  };

  return (
    <>
      <TextField
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon style={{ color: "var(--text-icon-regular)" }} />
            </InputAdornment>
          ),
        }}
        placeholder="Search by Order Details"
        value={input}
        onChange={handleTextChange}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            // Prevent's default 'Enter' behavior.
            event.defaultPrevented = true;
            triggerSearch(input);
          }
        }}
        sx={{
          width: "350px",
          "&:hover fieldset": {
            backgroundColor: "#ffffff",
          },
          "&.MuiFormControl-root .MuiInputBase-root": {
            borderRadius: "8px",
          },
          "&:hover fieldset": {
            color: "#000000",
          },
        }}
      />
    </>
  );
};

const CustomToolbar = () => {
  return (
    <>
      <GridToolbarContainer>
        <Box className="records-grid-toolbar-container">
          <Searchbar />
        </Box>
      </GridToolbarContainer>
    </>
  );
};

export default RecordsTab;
